import { useEffect, useState } from "react";
import "./HeroSlider.css";
import API_BASE_URL from "../config/config";

function HeroSlider() {
    const [slides, setSlides] = useState([]);
    const [current, setCurrent] = useState(0);
    const [loading, setLoading] = useState(true);
    const [direction, setDirection] = useState("next");
    const [isAnimating, setIsAnimating] = useState(false);
    const [imageErrors, setImageErrors] = useState({});

    const baseUrl = API_BASE_URL.replace(/\/+$/, "").replace(/\/api$/, "");

    /*
    |--------------------------------------------------------------------------
    | Build Image URL
    |--------------------------------------------------------------------------
    */

    const getImageUrl = (image, imageUrl) => {
        if (imageUrl && /^https?:\/\//i.test(imageUrl)) {
            return imageUrl;
        }

        const value = imageUrl || image;

        if (!value) return "";

        const cleanValue = String(value)
            .trim()
            .replace(/\\/g, "/");

        if (/^https?:\/\//i.test(cleanValue)) {
            return cleanValue;
        }

        const filename = cleanValue.split("/").filter(Boolean).pop();

        if (!filename) return "";

        return `${baseUrl}/storage/sliders/${encodeURIComponent(filename)}`;
    };

    /*
    |--------------------------------------------------------------------------
    | Fetch Public Sliders
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const controller = new AbortController();

        const fetchSliders = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/sliders`, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                    },
                    signal: controller.signal,
                });

                const data = await response.json();

                if (!response.ok || !data.success) {
                    throw new Error(
                        data.message || `Failed to fetch sliders (${response.status})`
                    );
                }

                const sliderData = Array.isArray(data.sliders)
                    ? data.sliders
                    : [];

                setSlides(sliderData);
                setCurrent(0);
                setImageErrors({});
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error("Slider API Error:", error);
                    setSlides([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchSliders();

        return () => controller.abort();
    }, []);

    /*
    |--------------------------------------------------------------------------
    | Change Slide
    |--------------------------------------------------------------------------
    */

    const changeSlide = (newIndex, slideDirection = "next") => {
        if (
            slides.length <= 1 ||
            isAnimating ||
            newIndex === current
        ) {
            return;
        }

        setDirection(slideDirection);
        setIsAnimating(true);
        setCurrent(newIndex);
    };

    // End animation after 800 ms.
    useEffect(() => {
        if (!isAnimating) return;

        const timeout = setTimeout(() => {
            setIsAnimating(false);
        }, 800);

        return () => clearTimeout(timeout);
    }, [isAnimating, current]);

    /*
    |--------------------------------------------------------------------------
    | Next / Previous
    |--------------------------------------------------------------------------
    */

    const nextSlide = () => {
        const nextIndex = (current + 1) % slides.length;
        changeSlide(nextIndex, "next");
    };

    const prevSlide = () => {
        const previousIndex =
            current === 0 ? slides.length - 1 : current - 1;

        changeSlide(previousIndex, "prev");
    };

    /*
    |--------------------------------------------------------------------------
    | Automatic Slider
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (slides.length <= 1 || isAnimating) return;

        const interval = setInterval(() => {
            setDirection("next");
            setCurrent((previous) => (previous + 1) % slides.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [slides.length, isAnimating]);

    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {
        return (
            <section className="hero-slider">
                <div className="hero-container">
                    <div className="slider-loading">Loading...</div>
                </div>
            </section>
        );
    }

    if (!slides.length) {
        return null;
    }

    const slide = slides[current];
    const imageUrl = getImageUrl(slide.image, slide.image_url);

    return (
        <section className="hero-slider">
            <div
                key={slide.id}
                className={`hero-slide ${
                    direction === "next" ? "slide-next" : "slide-prev"
                }`}
            >
                <div className="hero-container">
                    {/* LEFT CONTENT */}
                    <div className="hero-content">
                        {slide.badge && (
                            <div className="badge-custom">
                                {slide.badge}
                            </div>
                        )}

                        {slide.title && <h1>{slide.title}</h1>}

                        {slide.description && <p>{slide.description}</p>}
                    </div>

                    {/* RIGHT IMAGE */}
                    <div className="hero-image">
                        {imageUrl && !imageErrors[slide.id] ? (
                            <img
                                src={imageUrl}
                                alt={slide.title || "HomoeoAdda Slider"}
                                onError={() => {
                                    console.error(
                                        "Slider image failed to load:",
                                        imageUrl
                                    );

                                    setImageErrors((previous) => ({
                                        ...previous,
                                        [slide.id]: true,
                                    }));
                                }}
                            />
                        ) : (
                            <div className="slider-image-placeholder">
                                Image not available
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* PREVIOUS */}
            {slides.length > 1 && (
                <button
                    type="button"
                    className="slider-arrow slider-prev"
                    onClick={prevSlide}
                    aria-label="Previous slide"
                    disabled={isAnimating}
                >
                    &#10094;
                </button>
            )}

            {/* NEXT */}
            {slides.length > 1 && (
                <button
                    type="button"
                    className="slider-arrow slider-next"
                    onClick={nextSlide}
                    aria-label="Next slide"
                    disabled={isAnimating}
                >
                    &#10095;
                </button>
            )}

            {/* DOTS */}
            {slides.length > 1 && (
                <div className="slider-dots">
                    {slides.map((item, index) => (
                        <button
                            type="button"
                            key={item.id}
                            className={current === index ? "dot active" : "dot"}
                            onClick={() =>
                                changeSlide(
                                    index,
                                    index > current ? "next" : "prev"
                                )
                            }
                            aria-label={`Go to slide ${index + 1}`}
                            disabled={isAnimating}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

export default HeroSlider;
